import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { User, Lock, FileJson, Loader2, Download, Check, Copy, Paintbrush, Sun } from 'lucide-react';

import { SuperAdminLayout } from '../../components/SuperAdminLayout';
import ThemeToggle from '../../components/ThemeToggle';
import { useAuth } from '../../contexts/AuthContext';
import { useBranding } from '../../contexts/BrandingContext';
import { API_BASE_URL } from '../../lib/utils';
import api from '../../lib/api';

const SettingsPage = () => {
  const { refresh: refreshBranding } = useBranding();
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState('personal');

  const [profile, setProfile] = useState({ name: '', email: '' });
  const [passwords, setPasswords] = useState({ current_password: '', new_password: '', confirm_password: '' });

  const [endpoints, setEndpoints] = useState([]);
  const [loadingEndpoints, setLoadingEndpoints] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState(null);

  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  const [branding, setBranding] = useState({
    brand_name: '',
    slogan: '',
    company_name: '',
    website: '',
    logo_url: '',
    favicon_url: '',
  });
  const [savingBranding, setSavingBranding] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);

  useEffect(() => {
    if (user) {
      setProfile({ name: user.name || '', email: user.email || '' });
    }
  }, [user]);

  useEffect(() => {
    if (activeTab === 'postman') {
      fetchEndpoints();
    }
    if (activeTab === 'branding') {
      fetchBranding();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchEndpoints = async () => {
    setLoadingEndpoints(true);
    try {
      const response = await api.get('/superadmin/postman/endpoints');
      setEndpoints(response.data.endpoints || []);
    } catch (error) {
      toast.error('Failed to fetch endpoints');
    } finally {
      setLoadingEndpoints(false);
    }
  };

  const fetchBranding = async () => {
    try {
      const res = await api.get('/superadmin/settings/branding');
      const b = res.data?.branding || {};
      setBranding({
        brand_name: b.brand_name || '',
        slogan: b.slogan || '',
        company_name: b.company_name || '',
        website: b.website || '',
        logo_url: b.logo_url || '',
        favicon_url: b.favicon_url || '',
      });
    } catch (e) {
      toast.error('Failed to load branding');
    }
  };

  const handleSaveProfile = async () => {
    if (!profile.name.trim() || !profile.email.trim()) {
      toast.error('Name and email are required');
      return;
    }

    setSavingProfile(true);
    try {
      await api.put('/superadmin/profile', profile);
      toast.success('Profile updated');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwords.current_password || !passwords.new_password) {
      toast.error('Please fill all password fields');
      return;
    }
    if (passwords.new_password !== passwords.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }

    setSavingPassword(true);
    try {
      await api.post('/superadmin/change-password', {
        current_password: passwords.current_password,
        new_password: passwords.new_password,
      });
      toast.success('Password changed successfully');
      setPasswords({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setSavingPassword(false);
    }
  };

  const saveBranding = async () => {
    setSavingBranding(true);
    try {
      await api.put('/superadmin/settings/branding', {
        brand_name: branding.brand_name,
        slogan: branding.slogan,
        company_name: branding.company_name,
        website: branding.website,
      });
      await refreshBranding();
      toast.success('Branding updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update branding');
    } finally {
      setSavingBranding(false);
    }
  };

  const uploadBrandingFile = async (kind, file) => {
    const fd = new FormData();
    fd.append('file', file);

    if (kind === 'logo') setUploadingLogo(true);
    if (kind === 'favicon') setUploadingFavicon(true);

    try {
      const res = await api.post(`/superadmin/settings/branding/${kind}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = res.data?.logo_url || res.data?.favicon_url || '';
      setBranding((prev) => ({ ...prev, [`${kind}_url`]: url }));
      await refreshBranding();
      toast.success(`${kind === 'logo' ? 'Logo' : 'Favicon'} uploaded`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      if (kind === 'logo') setUploadingLogo(false);
      if (kind === 'favicon') setUploadingFavicon(false);
    }
  };

  const handleDownloadCollection = async (scope) => {
    const urlMap = {
      'new-web': '/superadmin/postman/download/new-web',
      'old-web': '/superadmin/postman/download/old-web',
      'old-mobile': '/superadmin/postman/download/old-mobile',
    };
    const filenameMap = {
      'new-web': 'wingsdiet-new-web-apis.postman_collection.json',
      'old-web': 'wingsdiet-old-web-apis.postman_collection.json',
      'old-mobile': 'wingsdiet-old-mobile-apis.postman_collection.json',
    };

    try {
      const response = await api.get(urlMap[scope], { responseType: 'blob' });
      const contentDisposition = response.headers['content-disposition'] || '';
      const match = contentDisposition.match(/filename=([^;]+)/i);
      const filename = match ? match[1].replace(/\"/g, '').trim() : filenameMap[scope];

      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/json' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to download collection');
    }
  };

  const copyToClipboard = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const tabs = [
    { id: 'personal', label: 'Personal Info', icon: User },
    { id: 'security', label: 'Security', icon: Lock },
    { id: 'appearance', label: 'Appearance', icon: Sun },
    { id: 'branding', label: 'Branding', icon: Paintbrush },
    { id: 'postman', label: 'Postman Collection', icon: FileJson },
  ];

  const getMethodColor = (method) => {
    switch (method) {
      case 'GET': return 'bg-green-100 text-green-700';
      case 'POST': return 'bg-blue-100 text-blue-700';
      case 'PUT': return 'bg-amber-100 text-amber-700';
      case 'DELETE': return 'bg-red-100 text-red-700';
      default: return 'bg-muted text-foreground';
    }
  };

  return (
    <SuperAdminLayout>
      <div className="p-8" data-testid="settings-page">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-foreground">Settings</h1>
          <p className="text-muted-foreground mt-1">Manage your account settings</p>
        </div>

        <div className="panel border border-border p-0 overflow-hidden">
          <div className="flex border-b border-border">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          <div className="p-6">
            {activeTab === 'personal' && (
              <div className="max-w-md space-y-4" data-testid="personal-info-tab">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Name</label>
                  <input
                    type="text"
                    value={profile.name}
                    onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                    className="w-full px-3 py-2 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-card"
                    data-testid="profile-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Email</label>
                  <input
                    type="email"
                    value={profile.email}
                    onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                    className="w-full px-3 py-2 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-card"
                    data-testid="profile-email-input"
                  />
                </div>
                <button
                  onClick={handleSaveProfile}
                  disabled={savingProfile}
                  className="btn btn-primary px-4 py-2 rounded-md text-sm font-semibold disabled:opacity-50 flex items-center gap-2"
                  data-testid="save-profile-btn"
                >
                  {savingProfile && <Loader2 className="w-4 h-4 animate-spin" />}
                  Save Changes
                </button>
              </div>
            )}

            {activeTab === 'security' && (
              <div className="max-w-md space-y-4" data-testid="security-tab">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Current Password</label>
                  <input
                    type="password"
                    value={passwords.current_password}
                    onChange={(e) => setPasswords({ ...passwords, current_password: e.target.value })}
                    className="w-full px-3 py-2 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-card"
                    data-testid="current-password-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">New Password</label>
                  <input
                    type="password"
                    value={passwords.new_password}
                    onChange={(e) => setPasswords({ ...passwords, new_password: e.target.value })}
                    className="w-full px-3 py-2 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-card"
                    data-testid="new-password-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Confirm New Password</label>
                  <input
                    type="password"
                    value={passwords.confirm_password}
                    onChange={(e) => setPasswords({ ...passwords, confirm_password: e.target.value })}
                    className="w-full px-3 py-2 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-card"
                    data-testid="confirm-password-input"
                  />
                </div>
                <button
                  onClick={handleChangePassword}
                  disabled={savingPassword}
                  className="btn btn-primary px-4 py-2 rounded-md text-sm font-semibold disabled:opacity-50 flex items-center gap-2"
                  data-testid="change-password-btn"
                >
                  {savingPassword && <Loader2 className="w-4 h-4 animate-spin" />}
                  Update Password
                </button>
              </div>
            )}

            {activeTab === 'appearance' && (
              <div className="max-w-md space-y-4" data-testid="appearance-tab">
                <div>
                  <h3 className="font-medium text-foreground">Appearance</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Choose light, dark, or match your system preference.
                  </p>
                </div>
                <div className="rounded-lg border border-border bg-muted/30 p-4">
                  <ThemeToggle showLabels />
                </div>
              </div>
            )}

            {activeTab === 'branding' && (
              <div className="max-w-2xl space-y-6" data-testid="branding-tab">
                <div>
                  <h3 className="font-medium text-foreground">Branding</h3>
                  <p className="text-sm text-muted-foreground mt-1">These settings apply across all panels and login pages.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">Brand Name</label>
                    <input
                      type="text"
                      value={branding.brand_name}
                      onChange={(e) => setBranding({ ...branding, brand_name: e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-card"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">Slogan / Tagline</label>
                    <input
                      type="text"
                      value={branding.slogan}
                      onChange={(e) => setBranding({ ...branding, slogan: e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-card"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">Company Name</label>
                    <input
                      type="text"
                      value={branding.company_name}
                      onChange={(e) => setBranding({ ...branding, company_name: e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-card"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">Website</label>
                    <input
                      type="url"
                      value={branding.website}
                      onChange={(e) => setBranding({ ...branding, website: e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-card"
                      placeholder="https://example.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="border border-border rounded-md p-4 bg-card">
                    <div className="text-sm font-medium text-foreground">Logo</div>
                    <div className="text-xs text-muted-foreground mt-1">Used in headers and login screens.</div>
                    <div className="mt-3 flex items-center gap-3">
                      <div className="w-14 h-14 rounded border border-border bg-muted flex items-center justify-center overflow-hidden">
                        {branding.logo_url ? (
                          <img
                            src={`${API_BASE_URL}${branding.logo_url}?v=${encodeURIComponent(String(branding.rev || 0))}`}
                            alt="Logo preview"
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <span className="text-xs text-muted-foreground">No logo</span>
                        )}
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) uploadBrandingFile('logo', f);
                        }}
                        disabled={uploadingLogo}
                      />
                    </div>
                    {uploadingLogo ? (
                      <div className="text-xs text-muted-foreground mt-2 flex items-center gap-2">
                        <Loader2 className="w-3 h-3 animate-spin" /> Uploading...
                      </div>
                    ) : null}
                  </div>

                  <div className="border border-border rounded-md p-4 bg-card">
                    <div className="text-sm font-medium text-foreground">Favicon</div>
                    <div className="text-xs text-muted-foreground mt-1">Used in the browser tab.</div>
                    <div className="mt-3 flex items-center gap-3">
                      <div className="w-14 h-14 rounded border border-border bg-muted flex items-center justify-center overflow-hidden">
                        {branding.favicon_url ? (
                          <img
                            src={`${API_BASE_URL}${branding.favicon_url}?v=${encodeURIComponent(String(branding.rev || 0))}`}
                            alt="Favicon preview"
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <span className="text-xs text-muted-foreground">No favicon</span>
                        )}
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) uploadBrandingFile('favicon', f);
                        }}
                        disabled={uploadingFavicon}
                      />
                    </div>
                    {uploadingFavicon ? (
                      <div className="text-xs text-muted-foreground mt-2 flex items-center gap-2">
                        <Loader2 className="w-3 h-3 animate-spin" /> Uploading...
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={saveBranding}
                    disabled={savingBranding}
                    className="btn btn-primary px-4 py-2 rounded-md text-sm font-semibold disabled:opacity-50 flex items-center gap-2"
                  >
                    {savingBranding && <Loader2 className="w-4 h-4 animate-spin" />}
                    Save Branding
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'postman' && (
              <div data-testid="postman-tab">
                <div className="mb-6 flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-foreground">Postman Collection</h3>
                    <p className="text-sm text-muted-foreground mt-1">Auto-generated from the FastAPI routes so it always stays up to date.</p>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => handleDownloadCollection('new-web')}
                      className="btn btn-primary flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold"
                      data-testid="download-new-web-btn"
                    >
                      <Download className="w-4 h-4" />
                      New Web APIs
                    </button>
                    <button
                      onClick={() => handleDownloadCollection('old-web')}
                      className="btn btn-outline-primary flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold border border-primary text-primary hover:bg-primary/5"
                      data-testid="download-old-web-btn"
                    >
                      <Download className="w-4 h-4" />
                      Old Web APIs
                    </button>
                    <button
                      onClick={() => handleDownloadCollection('old-mobile')}
                      className="btn flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold border border-[#506690] text-muted-foreground hover:bg-[#506690]/5"
                      data-testid="download-old-mobile-btn"
                    >
                      <Download className="w-4 h-4" />
                      Old Mobile APIs
                    </button>
                  </div>
                </div>

                <div className="border border-border rounded-md overflow-hidden bg-card">
                  <div className="px-4 py-3 bg-muted border-b border-border">
                    <h4 className="font-medium text-foreground">API Endpoints ({endpoints.length})</h4>
                  </div>

                  {loadingEndpoints ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-6 h-6 animate-spin text-primary" />
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100 max-h-[500px] overflow-y-auto">
                      {endpoints.map((endpoint, index) => (
                        <div key={index} className="px-4 py-3 hover:bg-muted">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${getMethodColor(endpoint.method)}`}>
                                {endpoint.method}
                              </span>
                              <code className="text-sm text-foreground font-mono">{endpoint.path}</code>
                              {endpoint.auth && (
                                <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-xs">Auth</span>
                              )}
                            </div>
                            <button
                              onClick={() => copyToClipboard(endpoint.path, index)}
                              className="p-1.5 hover:bg-muted rounded"
                              title="Copy path"
                            >
                              {copiedIndex === index ? (
                                <Check className="w-4 h-4 text-green-600" />
                              ) : (
                                <Copy className="w-4 h-4 text-muted-foreground" />
                              )}
                            </button>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1 ml-14">{endpoint.description}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </SuperAdminLayout>
  );
};

export default SettingsPage;
